import React, { useEffect, useMemo } from 'react'
import configment from '@adc/configment'
import { match } from 'ts-pattern'
import * as graph from '@adc/sdk'
import type { Maybe, UserVerification } from '@adc/sdk/types'
import { Button, CardContainer, Text, Icon, NullComponent } from '../elementals'
import { canInitializeVerification, errorCodeCopyMap } from './utils'

const { VerificationStatus } = graph.enums

const errorMessageCopy = (
  <>
    Don’t worry, give us a call and we’ll figure this out together. <br />
    <a href='tel:8007936107'>(800) 793-6107</a> Mon-Fri 5 a.m. - 5 p.m. PT
  </>
)

const getVerificationErrorContent = (onGoBack?: () => void) => {
  return {
    title: (
      <>
        <Icon name='times' iconStyle='regular' color='error' size='large' />{' '}
        Your ID Couldn't Be Verified
      </>
    ),
    content: () => errorMessageCopy,
    buttonProps: onGoBack
      ? {
          label: 'Close',
          onClick: onGoBack,
        }
      : undefined,
  }
}

type StatusContent = {
  title: React.ReactNode
  content: (message?: Maybe<string> | React.ReactNode) => React.ReactNode
  buttonProps?: {
    label: string
    onClick?: () => void
  }
}

type IdVerificationStatusProps = {
  error?: Maybe<string>
  isLoading?: boolean
  verificationStatus?: Maybe<UserVerification>
  fetchVerificationStatus: () => void
  onRetry?: () => void
  onGoBack?: () => void
  onContinue?: () => void
}

const IdVerificationStatus = ({
  error,
  isLoading,
  verificationStatus,
  fetchVerificationStatus,
  onRetry,
  onGoBack,
  onContinue,
}: IdVerificationStatusProps) => {
  useEffect(() => {
    const duration =
      Number(configment.get('ID_VERIFICATION_POLLING_INTERVAL', 5)) * 1000

    let timer: string | number | NodeJS.Timeout | undefined
    const runTimer = () => {
      if (
        !!error ||
        isLoading ||
        verificationStatus?.internal_verification_status !==
          VerificationStatus.VerificationInProgress
      ) {
        return
      }
      fetchVerificationStatus()
      timer = setTimeout(runTimer, duration)
    }
    timer = setTimeout(runTimer, duration)

    return () => clearTimeout(timer)
  }, [verificationStatus, isLoading, error, fetchVerificationStatus])

  const errorMessage = useMemo(() => {
    if (!verificationStatus) return null
    return (
      verificationStatus?.error_message ||
      errorCodeCopyMap[verificationStatus?.reject_reason || ''] ||
      errorMessageCopy
    )
  }, [verificationStatus])

  const status = useMemo<Maybe<StatusContent>>((): Maybe<StatusContent> => {
    if (!!error) {
      if (isLoading) return null
      const canRetry = canInitializeVerification(verificationStatus)
      return {
        ...getVerificationErrorContent(onGoBack),
        buttonProps: canRetry
          ? {
              label: 'Try Again',
              onClick: onRetry,
            }
          : undefined,
      }
    }
    const internalVerificationStatus =
      verificationStatus?.internal_verification_status
    return match(internalVerificationStatus)
      .with(VerificationStatus.VerificationInProgress, () => ({
        title: (
          <>
            <Icon
              name='sync-alt'
              iconStyle='regular'
              color='adcLightBlue'
              size='large'
              loading
            />{' '}
            Your ID Is Being Verified...
          </>
        ),
        content: () => (
          <>
            Please stay on this page while we verify your identity — it won’t
            take long.
            <br />
            If you’re in a rush, we’ll send you a confirmation email once you’ve
            been verified.
          </>
        ),
        buttonText: 'Close',
        onClick: onGoBack,
      }))
      .with(
        VerificationStatus.ApprovedManually,
        VerificationStatus.ApprovedVerified,
        () => ({
          title: (
            <>
              <Icon
                name='check'
                iconStyle='regular'
                color='success'
                size='large'
              />{' '}
              Congrats! You’ve Been Verified
            </>
          ),
          content: () =>
            'You’re one step closer to bidding and buying. Now let’s get back to business.',
          buttonProps: onContinue
            ? {
                label: 'Continue',
                onClick: onContinue,
              }
            : undefined,
        })
      )
      .with(VerificationStatus.RejectedOther, () => ({
        ...getVerificationErrorContent(),
        content: (error?: Maybe<string> | React.ReactNode) => error,
        buttonProps: {
          label: 'Try Again',
          onClick: onRetry,
        },
      }))
      .with(VerificationStatus.RejectedDuplicate, () => ({
        ...getVerificationErrorContent(onGoBack),
        content: () => (
          <>
            It looks like this ID is already associated with another account.{' '}
            <br />
            Don’t worry, give us a call and we’ll figure this out together.{' '}
            <br />
            <a href='tel:8007936107'>(800) 793-6107</a> Mon-Fri 5 a.m. - 5 p.m.
            PT
          </>
        ),
      }))
      .with(
        VerificationStatus.RejectedManually,
        VerificationStatus.RejectedFraud,
        VerificationStatus.TooManyAttempts,
        () => getVerificationErrorContent(onGoBack)
      )
      .otherwise((): Maybe<StatusContent> => null)
  }, [verificationStatus, error, isLoading])

  if (!status) return <NullComponent />

  return (
    <CardContainer
      dropShadow='small'
      borderRadius='extra-small'
      padding='extra-small'
    >
      <Text.Header
        className='u-tc'
        color='gray'
        level={2}
        data-elm-id='application_status_header'
      >
        {status.title}
      </Text.Header>
      <Text className='u-mb-2'>{status.content(errorMessage)}</Text>
      {status.buttonProps && (
        <div className='status-button-container'>
          <Button
            role='primary'
            onClick={status.buttonProps.onClick}
            elmId='identity_verification_page_return_button'
          >
            {status.buttonProps.label}
          </Button>
        </div>
      )}
    </CardContainer>
  )
}

export default IdVerificationStatus
